/**
 * Matching layout: tutors, sessions, and students with drag-and-drop.
 */
import * as React from 'react';
import {
  DndContext,
  closestCenter,
  pointerWithin,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  type CollisionDetection,
} from '@dnd-kit/core';
import { useQueryClient } from '@tanstack/react-query';
import { UsersListColumn } from '@/components/common/UsersListColumn';
import { MatchingTable, SessionHandlers } from './MatchingTable';
import { CustomDragOverlay } from '@/components/common/CustomDragOverlay';
import { SlidePanel } from '@/components/common/SlidePanel';
import { CreateStudentView } from '@/components/students/inperson/CreateStudentView';
import { CreateSessionView } from '@/components/sessions/inperson/CreateSessionView';
import { useConnectedUser } from '@/hooks/useConnectedUser';
import { prependUserToUsersSearchCaches } from '@/utils/created-user-profile-utils';
import type { UserWithProfile } from '@/types/user.types';
import {
  countTutorMatches,
  getExcludedStudentIds,
  getExcludedTutorIds,
  getMatchingTutorsPageSize,
  isUserInList,
  isVolunteerAlreadyOnExpandedSession,
  planStudentToTutorChipDrop,
  resolveMatchingDragKind,
  resolveMatchingDropTarget,
} from '../matching-utils';

/** Props for {@link ConfigureMatchingView}. */
interface ConfigureMatchingViewProps {
  selectedDates?: Date[];
  onSetSelectedDates?: (dates: Date[]) => void;
  onRequestCreateTutor?: () => void;
}

/** Three-column in-person matching: tutors, sessions, students. */
export const ConfigureMatchingView: React.FC<ConfigureMatchingViewProps> = ({
  selectedDates = [],
  onSetSelectedDates,
  onRequestCreateTutor,
}) => {
  const { user: connectedUser, isVolunteer } = useConnectedUser();
  const queryClient = useQueryClient();

  const [isStudentPanelOpen, setIsStudentPanelOpen] = React.useState(false);
  const [isCreateSessionOpen, setIsCreateSessionOpen] = React.useState(false);
  const [activeDragUser, setActiveDragUser] = React.useState<UserWithProfile | null>(null);
  const [activeDragType, setActiveDragType] = React.useState<'student' | 'tutor' | null>(null);
  const [currentExpandedSessionId, setCurrentExpandedSessionId] = React.useState<string | null>(
    null,
  );
  const [participantsVersion, setParticipantsVersion] = React.useState(0);
  const sessionHandlersRef = React.useRef<Map<string, SessionHandlers>>(new Map());

  /** Let each collapse register add/match getters so columns can exclude current participants. */
  const registerSessionHandlers = React.useCallback(
    (sessionId: string, handlers: SessionHandlers) => {
      sessionHandlersRef.current.set(sessionId, handlers);
    },
    [],
  );

  /** Only one session is expanded; columns filter against that session. */
  const handleSessionToggled = React.useCallback((expandedSessionId: string | null) => {
    setCurrentExpandedSessionId(expandedSessionId);
  }, []);

  /** Bump so excluded-id memos re-read handlers after a drop or add. */
  const handleParticipantsChange = React.useCallback(() => {
    setParticipantsVersion((v) => v + 1);
  }, []);

  /** Tutors already on the expanded session (hide from the left column). */
  const excludedTutorIds = React.useMemo(() => {
    if (!currentExpandedSessionId) return [];
    return getExcludedTutorIds(sessionHandlersRef.current.get(currentExpandedSessionId));
  }, [currentExpandedSessionId, participantsVersion]);

  /** True when the connected volunteer is already a participant (no pin slot needed). */
  const isConnectedVolunteerInParticipants = React.useMemo(
    () =>
      isVolunteerAlreadyOnExpandedSession(
        isVolunteer,
        connectedUser?.id,
        currentExpandedSessionId,
        excludedTutorIds,
      ),
    [isVolunteer, connectedUser, currentExpandedSessionId, excludedTutorIds],
  );

  /** Shrink tutor page size only when the connected volunteer will be pinned (not already a participant). */
  const tutorsPageSize = getMatchingTutorsPageSize(isVolunteer, isConnectedVolunteerInParticipants);

  /** Temp students plus already-matched students (hide from the right column). */
  const excludedStudentIds = React.useMemo(() => {
    if (!currentExpandedSessionId) return [];
    return getExcludedStudentIds(sessionHandlersRef.current.get(currentExpandedSessionId));
  }, [currentExpandedSessionId, participantsVersion]);

  /** Match badge on tutor cards in the left column. */
  const getTutorMatchCount = React.useCallback(
    (tutorId: string): number => {
      if (!currentExpandedSessionId) return 0;
      return countTutorMatches(sessionHandlersRef.current.get(currentExpandedSessionId), tutorId);
    },
    [currentExpandedSessionId, participantsVersion],
  );

  /** Double-click adds the tutor to the expanded session. */
  const handleTutorDoubleClick = React.useCallback(
    (user: UserWithProfile) => {
      if (!currentExpandedSessionId) return;
      const handlers = sessionHandlersRef.current.get(currentExpandedSessionId);
      handlers?.handleAddTutor?.(user);
    },
    [currentExpandedSessionId],
  );

  /** Double-click adds the student unless they are already a participant. */
  const handleStudentDoubleClick = React.useCallback(
    (user: UserWithProfile) => {
      if (!currentExpandedSessionId) return;
      const handlers = sessionHandlersRef.current.get(currentExpandedSessionId);
      if (handlers?.handleAddStudent && handlers?.getTempStudents) {
        if (!isUserInList(handlers.getTempStudents(), user.id)) {
          handlers.handleAddStudent(user);
        }
      }
    },
    [currentExpandedSessionId],
  );

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  /** Prefer the droppable under the pointer so {@link UnifiedDropZone} does not steal TutorChip drops. */
  const collisionDetection: CollisionDetection = React.useCallback((args) => {
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) return pointerCollisions;
    return closestCenter(args);
  }, []);

  /** Resolve tutor vs student from the drag payload (`roleGroup` or `type`). */
  const handleDragStart = (event: DragStartEvent) => {
    const resolved = resolveMatchingDragKind(event.active.data.current);
    if (!resolved) return;
    setActiveDragUser(resolved.user);
    setActiveDragType(resolved.kind);
  };

  /** Dropping a student on a tutor chip rematches them (unmatch old tutor first). */
  const handleStudentToTutorChipDrop = React.useCallback(
    (student: UserWithProfile, sessionId: string, tutorId: string) => {
      const handlers = sessionHandlersRef.current.get(sessionId);
      if (
        !handlers?.handleMatchStudentToTutor ||
        !handlers?.handleAddStudent ||
        !handlers?.getTempStudents ||
        !handlers?.getSpecificMatches ||
        !handlers?.handleUnmatchStudent
      ) {
        return;
      }

      const plan = planStudentToTutorChipDrop({
        studentId: student.id,
        tutorId,
        tempStudents: handlers.getTempStudents(),
        matches: handlers.getSpecificMatches(),
      });
      if (plan.unmatchTutorId) {
        handlers.handleUnmatchStudent(student.id, plan.unmatchTutorId);
      }
      if (plan.shouldAddStudent) {
        handlers.handleAddStudent(student);
      }

      handlers.handleMatchStudentToTutor(student.id, tutorId);
      setParticipantsVersion((v) => v + 1);
    },
    [],
  );

  /** Drop a tutor onto the unified drop zone. */
  const handleTutorToDropzoneDrop = React.useCallback(
    (tutor: UserWithProfile, sessionId: string) => {
      const handlers = sessionHandlersRef.current.get(sessionId);
      if (handlers?.handleAddTutor) {
        handlers.handleAddTutor(tutor);
        setParticipantsVersion((v) => v + 1);
      }
    },
    [],
  );

  /** Drop a student onto the unified drop zone (skip if already a participant). */
  const handleStudentToDropzoneDrop = React.useCallback(
    (student: UserWithProfile, sessionId: string) => {
      const handlers = sessionHandlersRef.current.get(sessionId);
      if (!handlers?.handleAddStudent || !handlers?.getTempStudents) {
        return;
      }

      if (!isUserInList(handlers.getTempStudents(), student.id)) {
        handlers.handleAddStudent(student);
      }
      setParticipantsVersion((v) => v + 1);
    },
    [],
  );

  /** Route the drop: student→tutor-chip, tutor→dropzone, or student→dropzone. */
  const handleDragEnd = (event: DragEndEvent) => {
    const { over } = event;
    const activeUser = activeDragUser;
    const activeType = activeDragType;

    setActiveDragUser(null);
    setActiveDragType(null);

    if (!over || !activeUser || !activeType) return;

    const target = resolveMatchingDropTarget(activeType, over.data.current);
    if (!target) return;

    if (target.kind === 'student-to-tutor-chip') {
      handleStudentToTutorChipDrop(activeUser, target.sessionId, target.tutorId);
      return;
    }
    if (target.kind === 'tutor-to-dropzone') {
      handleTutorToDropzoneDrop(activeUser, target.sessionId);
      return;
    }
    handleStudentToDropzoneDrop(activeUser, target.sessionId);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="matching-configure-container">
        {isCreateSessionOpen ? (
          <CreateSessionView
            showParticipants
            onClose={() => setIsCreateSessionOpen(false)}
            onSuccess={() => {
              setIsCreateSessionOpen(false);
              void queryClient.invalidateQueries({ queryKey: ['sessions-matching'] });
            }}
            className="h-full"
          />
        ) : (
          <div className="matching-configure-layout">
            <div className="matching-column-left flex flex-col gap-2">
              <UsersListColumn
                role="tutor"
                emptyMessage={
                  currentExpandedSessionId
                    ? 'Aucun tuteur disponible pour cette séance'
                    : 'Recherchez un tuteur ou ouvrez une séance'
                }
                excludedIds={excludedTutorIds}
                pageSize={tutorsPageSize}
                getMatchCount={getTutorMatchCount}
                onDoubleClick={handleTutorDoubleClick}
                onCreateClick={onRequestCreateTutor}
                className="flex-1 min-h-0"
              />
            </div>

            <MatchingTable
              selectedDates={selectedDates}
              onRegisterSessionHandlers={registerSessionHandlers}
              onSessionToggled={handleSessionToggled}
              onParticipantsChange={handleParticipantsChange}
              onRequestCreateSession={() => setIsCreateSessionOpen(true)}
              onSetSelectedDates={onSetSelectedDates}
            />

            <div className="matching-column-right flex flex-col gap-2">
              <UsersListColumn
                role="student"
                emptyMessage={
                  currentExpandedSessionId
                    ? 'Aucun élève disponible pour cette séance'
                    : 'Recherchez un élève ou ouvrez une séance'
                }
                excludedIds={excludedStudentIds}
                pageSize={10}
                onDoubleClick={handleStudentDoubleClick}
                onCreateClick={() => setIsStudentPanelOpen(true)}
                className="flex-1 min-h-0"
              />
            </div>
          </div>
        )}
      </div>

      <CustomDragOverlay activeUser={activeDragUser} />

      <SlidePanel isOpen={isStudentPanelOpen} onClose={() => setIsStudentPanelOpen(false)}>
        <CreateStudentView
          onClose={() => setIsStudentPanelOpen(false)}
          onSuccess={(created) => {
            setIsStudentPanelOpen(false);
            prependUserToUsersSearchCaches(queryClient, 'student', created);
          }}
        />
      </SlidePanel>
    </DndContext>
  );
};
